import { anmeldekopf, basisUrl, rufe } from './client';
import type { KiVorschlag } from './ki';

/** SUP-17: der Bearbeitungsstand eines Vorgangs. */
export const VORGANGSSTATUS = [
  'neu', 'in_bearbeitung', 'wartet_auf_kunde', 'wartet_intern', 'geloest', 'geschlossen',
] as const;
export type Vorgangsstatus = (typeof VORGANGSSTATUS)[number];

export const STATUSNAME: Readonly<Record<Vorgangsstatus, string>> = {
  neu: 'neu',
  in_bearbeitung: 'in Bearbeitung',
  wartet_auf_kunde: 'wartet auf Kunde',
  wartet_intern: 'wartet intern',
  geloest: 'gelöst',
  geschlossen: 'geschlossen',
};

export type Nachrichtenart = 'nachricht' | 'abwesenheit' | 'automatisch' | 'zustellbericht' | 'massenmail';

export const ARTNAME: Readonly<Record<Nachrichtenart, string>> = {
  nachricht: 'Nachricht',
  abwesenheit: 'Abwesenheitsnotiz',
  automatisch: 'automatische Antwort',
  zustellbericht: 'Zustellbericht',
  massenmail: 'Werbung oder Massenversand',
};

export type Anhangszustand = 'abgelegt' | 'gesperrt' | 'zu_gross' | 'nicht_abgelegt';

/** SUP-07: wie sicher eine Zuordnung ist. */
export type Sicherheit = 'sicher' | 'wahrscheinlich' | 'unsicher';

/** SUP-07, SUP-09, SUP-13: wie der Vorgang zu seinem Kunden kam — oder warum er keinen hat. */
export type Zuordnungsstand = 'ausstehend' | 'automatisch' | 'bearbeiter' | 'vorschlag' | 'klaerung';

export const ZUORDNUNGSNAME: Readonly<Record<Zuordnungsstand, string>> = {
  ausstehend: 'noch nicht geprüft',
  automatisch: 'automatisch zugeordnet',
  bearbeiter: 'von Hand zugeordnet',
  vorschlag: 'Vorschlag wählen',
  klaerung: 'zu klären',
};

/** SUP-08: die Belegarten, die ein Vorgang als Bezug trägt. */
export type Bezugsart = 'buchung' | 'angebot' | 'auftrag' | 'rechnung' | 'termin' | 'gutschein';

export const BEZUGSNAME: Readonly<Record<Bezugsart, string>> = {
  buchung: 'Buchung',
  angebot: 'Angebot',
  auftrag: 'Auftrag',
  rechnung: 'Rechnung',
  termin: 'Termin zu',
  gutschein: 'Gutschein',
};

/** SUP-01: der Stand des Abrufs je Postfach — ohne Werte der Zugangsdaten. */
export type Posteingangsstand = {
  readonly kennung: string;
  readonly bezeichnung: string;
  /** graph oder demo (CRM-183). */
  readonly adapter: string;
  readonly eingerichtet: boolean;
  /** CRM-183: warum das Postfach nicht angebunden ist, etwa Demo außerhalb einer Demo-Instanz. */
  readonly hinweis: string | null;
  readonly variablen: readonly string[];
  readonly gelaufenAm: string | null;
  readonly erfolgreichAm: string | null;
  readonly fehler: string | null;
  readonly naechsterAbrufAb: string | null;
  readonly ungelesen: number;
  readonly aussortiert: number;
};

export type Vorgangszeile = {
  readonly id: string;
  readonly nummer: string;
  readonly postfach: string;
  readonly absender: string;
  readonly absendername: string | null;
  readonly betreff: string;
  readonly status: Vorgangsstatus;
  readonly eingangAm: string;
  readonly letzteNachrichtAm: string;
  readonly gelesen: boolean;
  readonly nachrichten: number;
  readonly anhaenge: number;
  readonly auszug: string;
  readonly kunde: string | null;
  /** Kundennummer und Name. */
  readonly kundenname: string | null;
  readonly zuordnung: Zuordnungsstand;
  readonly sicherheit: Sicherheit | null;
  /** SUP-17, SUP-18, SUP-25: Zuständigkeit, Fälligkeit, Kategorie, Ziele (CRM-178). */
  readonly bearbeitung: Listenstand;
};

/** SUP-14: das Anliegen; dieselbe Liste wie die Domäne fach.supportkategorie (0333). */
export const KATEGORIEN = [
  'buchungsaenderung', 'stornierung', 'rechnungsfrage', 'terminanfrage',
  'beschwerde', 'angebotsanfrage', 'sonstiges',
] as const;
export type Kategorie = (typeof KATEGORIEN)[number];

export const KATEGORIENAME: Readonly<Record<Kategorie, string>> = {
  buchungsaenderung: 'Buchungsänderung',
  stornierung: 'Stornierung',
  rechnungsfrage: 'Rechnungsfrage',
  terminanfrage: 'Terminanfrage',
  beschwerde: 'Beschwerde',
  angebotsanfrage: 'Angebotsanfrage',
  sonstiges: 'Sonstiges',
};

/** SUP-18: Person, Team (Rolle) oder niemand — dann liegt der Vorgang in der Warteschlange. */
export type Zustaendigkeit = {
  readonly benutzer: string | null;
  readonly benutzername: string | null;
  readonly rolle: string | null;
  readonly rollenname: string | null;
  /** SUP-26: die Person ist abwesend; wer sie gerade vertritt (BEN-10), sonst null. */
  readonly vertretenDurch: string | null;
  readonly vertreterName: string | null;
};

/** SUP-25: Stand eines Ziels; null, wenn das Postfach keines setzt. */
export type Zielstand = {
  readonly bis: string;
  /** Minuten bis zum Ziel; negativ heißt überschritten. Erreicht: null. */
  readonly restMinuten: number | null;
  readonly erreichtAm: string | null;
  readonly ueberschritten: boolean;
};

/** SUP-48: der Kunde hat einen offenen Fall aus einer Terminabsage. */
export type Absagehinweis = { readonly termin: string; readonly datum: string; readonly produkt: string };

/** Was die Liste je Vorgang zur Bearbeitung zeigt (Vorgangszeile.bearbeitung). */
export type Listenstand = {
  readonly zustaendigkeit: Zustaendigkeit;
  readonly faelligAm: string | null;
  /** SUP-14: vom Bearbeiter gesetzt, sonst null. */
  readonly kategorie: Kategorie | null;
  /** SUP-14: der Vorschlag der Regel, wenn keine Kategorie gesetzt ist. */
  readonly kategorieVorschlag: Kategorie | null;
  readonly reaktion: Zielstand | null;
  readonly loesung: Zielstand | null;
  readonly absage: Absagehinweis | null;
  /** SUP-22: jemand anderes hat den Vorgang gerade offen. */
  readonly bearbeitetVon: string | null;
};

/** SUP-18: „meine" (mir oder meiner Vertretung zugewiesen), „warteschlange" (niemand oder eine meiner Rollen). */
export type Listenansicht = 'alle' | 'meine' | 'warteschlange';

/** SUP-25, Glocke: ein offener Vorgang für den Zuständigen (oder die Eskalationsrolle). */
export type OffenerVorgang = {
  readonly id: string;
  readonly nummer: string;
  readonly betreff: string;
  readonly postfach: string;
  readonly faelligAm: string | null;
  readonly ueberfaellig: boolean;
  readonly eskaliert: boolean;
  /** SUP-26: Vorgang einer Person, die ich gerade vertrete. */
  readonly vertretungFuer: string | null;
};

export type Anhangszeile = {
  readonly id: string;
  readonly dateiname: string;
  readonly inhaltstyp: string;
  readonly groesse: number;
  readonly zustand: Anhangszustand;
  readonly grund: string | null;
  readonly eingebettet: boolean;
};

export type Nachrichtzeile = {
  readonly id: string;
  readonly richtung: 'eingehend' | 'ausgehend';
  readonly art: Nachrichtenart;
  readonly absender: string;
  readonly absendername: string | null;
  readonly an: readonly string[];
  readonly kopie: readonly string[];
  readonly betreff: string;
  readonly eingegangenAm: string;
  readonly textRein: string | null;
  /** Beim Eingang bereinigt; externe Bilder stehen in `data-extern-src`. */
  readonly textHtml: string | null;
  readonly anhaenge: readonly Anhangszeile[];
};

export type Aussortiertzeile = {
  readonly id: string;
  readonly postfach: string;
  readonly absender: string;
  readonly absendername: string | null;
  readonly betreff: string;
  readonly eingegangenAm: string;
  readonly art: Nachrichtenart;
  readonly grund: string;
  readonly auszug: string;
};

export type Kundenkurz = {
  readonly id: string;
  readonly kundennummer: string;
  readonly name: string;
  readonly kundenart: string;
  readonly status: string;
};

export type Vorschlagszeile = {
  readonly kunde: string;
  readonly kundennummer: string;
  readonly name: string;
  readonly kundenart: string;
  readonly status: string;
  readonly sicherheit: Sicherheit;
  readonly grund: string;
};

export type Bezugszeile = {
  readonly id: string;
  readonly art: Bezugsart;
  readonly ziel: string;
  readonly nummer: string;
  readonly herkunft: 'erkannt' | 'bearbeiter';
};

export type Verlaufshandlung =
  | 'automatisch' | 'vorschlag' | 'klaerung' | 'zugeordnet' | 'umgeordnet' | 'bestaetigt' | 'geloest'
  | 'kunde_angelegt' | 'bezug_hinzu' | 'bezug_weg';

export const HANDLUNGSNAME: Readonly<Record<Verlaufshandlung, string>> = {
  automatisch: 'automatisch zugeordnet',
  vorschlag: 'Vorschläge ermittelt',
  klaerung: 'in die Klärungsliste',
  zugeordnet: 'zugeordnet',
  umgeordnet: 'umgeordnet',
  bestaetigt: 'Zuordnung bestätigt',
  geloest: 'Zuordnung gelöst',
  kunde_angelegt: 'Kunde angelegt und zugeordnet',
  bezug_hinzu: 'Bezug verknüpft',
  bezug_weg: 'Bezug entfernt',
};

/** SUP-12: ein Eintrag im Verlauf der Zuordnung. */
export type Verlaufszeile = {
  readonly am: string;
  readonly wer: string;
  readonly handlung: Verlaufshandlung;
  /** SUP-09: die Stufe einer automatischen Zuordnung; sonst leer. */
  readonly sicherheit: Sicherheit | null;
  readonly kundeVorher: string | null;
  readonly kundeNachher: string | null;
  readonly bezug: string | null;
  readonly grund: string | null;
};

export type Zuordnungsansicht = {
  readonly zuordnung: Zuordnungsstand;
  readonly kunde: Kundenkurz | null;
  readonly sicherheit: Sicherheit | null;
  readonly grund: string | null;
  /** Vertrieb: Firmenkunden, Info: Privatkunden und Service (fach.postfach.kundenkreis). */
  readonly kundenkreis: 'firmenkunden' | 'privatkunden' | null;
  readonly vorschlaege: readonly Vorschlagszeile[];
  readonly bezuege: readonly Bezugszeile[];
  readonly verlauf: readonly Verlaufszeile[];
};

/** KUN-23: ein Dublettenverdacht. */
export type Dublettentreffer = {
  readonly kunde: string;
  readonly kundennummer: string;
  readonly name: string;
  readonly ort: string | null;
  readonly guete: number;
  readonly grund: string;
};

/** SUP-10: die Vorbelegung fuer "Als Interessent anlegen". */
export type Kundenvorbelegung = {
  readonly kundenart: 'privat' | 'geschaeft';
  readonly firma: string;
  readonly vorname: string;
  readonly nachname: string;
  readonly email: string;
  readonly telefon: string;
  readonly dubletten: readonly Dublettentreffer[];
};

export type Vorgangsfilter = {
  /** Leer: alle Postfaecher — so liest die Klaerungsliste. */
  readonly postfach: string | null;
  /** SUP-13: "offen" heisst Vorschlag oder Klaerung; SUP-09: "unsicher" nur ueber Name oder Domaene. */
  readonly zuordnung?: 'offen' | 'unsicher' | Zuordnungsstand | null;
  readonly suche?: string;
  readonly status?: Vorgangsstatus | null;
  readonly nurUngelesen?: boolean;
  /** SUP-18: fehlt sie, „alle". */
  readonly ansicht?: Listenansicht;
  readonly kategorie?: Kategorie | null;
};

export async function posteingangsstand(): Promise<{
  readonly postfaecher: readonly Posteingangsstand[];
  readonly taktMinuten: number;
}> {
  return rufe('/support/postfaecher');
}

/** CRM-183: die Vorlagen für „Demo-Mail einspielen“. */
export const DEMO_VORLAGEN = ['anfrage_privat', 'firmenanfrage', 'antwort_vorgang', 'abwesenheit', 'werbung'] as const;
export type Demovorlage = (typeof DEMO_VORLAGEN)[number];

export type Demovorlagentext = {
  readonly schluessel: Demovorlage;
  readonly bezeichnung: string;
  readonly absender: string;
  readonly absendername: string | null;
  readonly betreff: string;
  readonly text: string;
};

export type Demomail = {
  readonly vorlage?: Demovorlage;
  readonly absender?: string;
  readonly absendername?: string;
  readonly betreff?: string;
  readonly text?: string;
};

export type Demoergebnis = {
  readonly ergebnis: 'aufgenommen' | 'angehaengt' | 'aussortiert' | 'doppelt';
  readonly vorgang: string | null;
  readonly nummer: string | null;
};

export async function demoVorlagen(): Promise<readonly Demovorlagentext[]> {
  return (await rufe<{ readonly vorlagen: readonly Demovorlagentext[] }>('/support/postfaecher/demo-vorlagen')).vorlagen;
}

/** CRM-183: eine Demo-Mail einspielen; sie nimmt denselben Weg wie eine Mail aus Graph. */
export async function demoMailEinspielen(postfach: string, mail: Demomail): Promise<Demoergebnis> {
  return rufe(`/support/postfaecher/${encodeURIComponent(postfach)}/demo-mail`, { verfahren: 'POST', rumpf: mail });
}

export async function vorgaenge(f: Vorgangsfilter): Promise<{ readonly vorgaenge: readonly Vorgangszeile[]; readonly gesamt: number }> {
  const abfrage = new URLSearchParams();
  if (f.postfach !== null) abfrage.set('postfach', f.postfach);
  if (f.zuordnung !== undefined && f.zuordnung !== null) abfrage.set('zuordnung', f.zuordnung);
  if (f.suche !== undefined && f.suche.trim() !== '') abfrage.set('suche', f.suche.trim());
  if (f.status !== undefined && f.status !== null) abfrage.set('status', f.status);
  if (f.nurUngelesen === true) abfrage.set('ungelesen', 'ja');
  if (f.ansicht !== undefined && f.ansicht !== 'alle') abfrage.set('ansicht', f.ansicht);
  if (f.kategorie !== undefined && f.kategorie !== null) abfrage.set('kategorie', f.kategorie);
  return rufe(`/support/vorgaenge?${abfrage.toString()}`);
}

/** SUP-25, SUP-26: meine offenen Vorgänge samt Vertretung und Eskalation, für Glocke und Arbeitsplatz. */
export async function meineVorgaenge(): Promise<readonly OffenerVorgang[]> {
  return rufe('/support/meine');
}

export async function vorgang(id: string): Promise<{
  readonly vorgang: Vorgangszeile;
  readonly nachrichten: readonly Nachrichtzeile[];
}> {
  return rufe(`/support/vorgaenge/${encodeURIComponent(id)}`);
}

export async function gelesenSetzen(id: string, gelesen: boolean): Promise<void> {
  await rufe(`/support/vorgaenge/${encodeURIComponent(id)}/gelesen`, { verfahren: 'PUT', rumpf: { gelesen } });
}

export async function statusSetzen(id: string, status: Vorgangsstatus): Promise<void> {
  await rufe(`/support/vorgaenge/${encodeURIComponent(id)}/status`, { verfahren: 'PUT', rumpf: { status } });
}

export async function aussortiert(postfach: string | null): Promise<{
  readonly nachrichten: readonly Aussortiertzeile[];
  readonly gesamt: number;
}> {
  return rufe(`/support/aussortiert${postfach === null ? '' : `?postfach=${encodeURIComponent(postfach)}`}`);
}

export async function nachricht(id: string): Promise<Nachrichtzeile> {
  return rufe(`/support/nachrichten/${encodeURIComponent(id)}`);
}

export async function uebernehmen(id: string): Promise<{ readonly vorgang: string; readonly nummer: string }> {
  return rufe(`/support/nachrichten/${encodeURIComponent(id)}/uebernehmen`, { verfahren: 'POST' });
}

/**
 * SUP-06: einen Anhang holen. Mit Anmeldung, deshalb nicht als einfacher
 * Verweis — der Browser bekommt ihn als Blob und öffnet ihn selbst.
 */
export async function anhangHolen(id: string): Promise<Blob> {
  const antwort = await fetch(`${basisUrl()}/support/anhaenge/${encodeURIComponent(id)}`, { headers: anmeldekopf() });
  if (!antwort.ok) throw new Error('Der Anhang ließ sich nicht öffnen.');
  return antwort.blob();
}

const vorgangspfad = (id: string): string => `/support/vorgaenge/${encodeURIComponent(id)}`;

/** SUP-07 bis SUP-09, SUP-12: Kunde, Vorschlaege, Bezuege und Verlauf eines Vorgangs. */
export async function zuordnung(id: string): Promise<Zuordnungsansicht> {
  return rufe(`${vorgangspfad(id)}/zuordnung`);
}

/** SUP-09, SUP-12: zuordnen oder umordnen; `angelegt`, wenn der Kunde eben aus der Nachricht entstand (SUP-10). */
export async function kundeSetzen(
  id: string,
  kunde: string,
  grund: string | null,
  angelegt = false,
): Promise<Zuordnungsansicht> {
  return rufe(`${vorgangspfad(id)}/kunde`, {
    verfahren: 'PUT',
    rumpf: { kunde, ...(grund === null || grund.trim() === '' ? {} : { grund: grund.trim() }), angelegt },
  });
}

/** SUP-12, SUP-13: den Kunden abnehmen; der Vorgang geht in die Klärungsliste. */
export async function kundeLoesen(id: string, grund: string): Promise<Zuordnungsansicht> {
  return rufe(`${vorgangspfad(id)}/kunde`, { verfahren: 'DELETE', rumpf: { grund } });
}

/** SUP-09: eine automatische Zuordnung bestaetigen; danach gilt sie als von Hand. */
export async function bestaetigen(id: string): Promise<Zuordnungsansicht> {
  return rufe(`${vorgangspfad(id)}/bestaetigen`, { verfahren: 'POST' });
}

export async function neuPruefen(id: string): Promise<Zuordnungsansicht> {
  return rufe(`${vorgangspfad(id)}/neu-pruefen`, { verfahren: 'POST' });
}

/** SUP-08: einen Beleg über seine Nummer verknüpfen. */
export async function bezugHinzu(id: string, nummer: string): Promise<Zuordnungsansicht> {
  return rufe(`${vorgangspfad(id)}/bezuege`, { verfahren: 'POST', rumpf: { nummer } });
}

export async function bezugWeg(id: string, bezug: string): Promise<Zuordnungsansicht> {
  return rufe(`${vorgangspfad(id)}/bezuege/${encodeURIComponent(bezug)}`, { verfahren: 'DELETE' });
}

/** SUP-10, KUN-23: was "Als Interessent anlegen" vorbelegt, samt Dublettenverdacht. */
export async function vorbelegung(id: string): Promise<Kundenvorbelegung> {
  return rufe(`${vorgangspfad(id)}/vorbelegung`);
}

/** SUP-11: die Vorgänge eines Kunden, neueste zuerst. */
export async function kommunikation(kunde: string): Promise<{ readonly vorgaenge: readonly Vorgangszeile[] }> {
  return rufe(`/support/kunden/${encodeURIComponent(kunde)}/kommunikation`);
}

// ---------------------------------------------------------------------------
// Bearbeitung, Antwort, Notiz, Folgeaktion (SUP-14 bis SUP-27; Auftrag CRM-178)
// ---------------------------------------------------------------------------

/** SUP-23: was aus dem Vorgang angestoßen werden kann. */
export const FOLGEAKTIONEN = [
  'angebot', 'buchung_aendern', 'buchung_stornieren', 'rechnung_senden', 'teilnehmer_aendern', 'wiedervorlage',
] as const;
export type Folgeaktionsart = (typeof FOLGEAKTIONEN)[number];
export type Folgeaktionsziel = 'angebot' | 'buchung' | 'rechnung' | 'wiedervorlage';

export const FOLGEAKTIONSNAME: Readonly<Record<Folgeaktionsart, string>> = {
  angebot: 'Angebot erstellen',
  buchung_aendern: 'Buchung ändern',
  buchung_stornieren: 'Buchung stornieren',
  rechnung_senden: 'Rechnung erneut senden',
  teilnehmer_aendern: 'Teilnehmer ändern',
  wiedervorlage: 'Wiedervorlage anlegen',
};

/** SUP-22: wer den Vorgang außer mir gerade offen hat. */
export type Bearbeitender = {
  readonly benutzer: string;
  readonly name: string;
  readonly seit: string;
};

/** SUP-21: interne Notiz — geht nie an den Kunden. */
export type Notizzeile = {
  readonly id: string;
  readonly text: string;
  readonly von: string;
  readonly am: string;
};

export type Ereignisart =
  | 'uebernommen' | 'zugewiesen' | 'status' | 'faelligkeit' | 'kategorie' | 'beantwortet'
  | 'eingangsbestaetigung' | 'folgeaktion' | 'zusammengefuehrt' | 'aufgeteilt';

export const EREIGNISNAME: Readonly<Record<Ereignisart, string>> = {
  uebernommen: 'übernommen',
  zugewiesen: 'zugewiesen',
  status: 'Status geändert',
  faelligkeit: 'Fälligkeit geändert',
  kategorie: 'Kategorie geändert',
  beantwortet: 'beantwortet',
  eingangsbestaetigung: 'Eingangsbestätigung gesendet',
  folgeaktion: 'Folgeaktion',
  zusammengefuehrt: 'zusammengeführt',
  aufgeteilt: 'Nachricht abgetrennt',
};

export type Ereigniszeile = {
  readonly am: string;
  readonly wer: string;
  readonly art: Ereignisart;
  readonly daten: Readonly<Record<string, unknown>>;
};

export type Folgeaktionszeile = {
  readonly id: string;
  readonly art: Folgeaktionsart;
  readonly stand: 'angestossen' | 'erledigt';
  readonly zielArt: Folgeaktionsziel | null;
  readonly ziel: string | null;
  readonly nummer: string | null;
  readonly von: string | null;
  readonly am: string;
};

/** SUP-19: Zustand einer ausgehenden Antwort im Postausgang. */
export type Versandstand = 'bereit' | 'laeuft' | 'uebernommen' | 'aufgegeben';

export const VERSANDNAME: Readonly<Record<Versandstand, string>> = {
  bereit: 'wartet auf Versand',
  laeuft: 'wird gesendet',
  uebernommen: 'gesendet',
  aufgegeben: 'nicht gesendet',
};

/** `demo`: von der Demo-Anbindung angenommen, nie zugestellt (CRM-183). */
export type Versandzeile = { readonly stand: Versandstand; readonly fehler: string | null; readonly demo: boolean };

export function versandname(v: Versandzeile): string {
  return v.demo && v.stand === 'uebernommen' ? 'gesendet (Demo)' : VERSANDNAME[v.stand];
}

/** GET /support/vorgaenge/:id/bearbeitung — die Bearbeitung eines Vorgangs für die Maske. */
export type Bearbeitungsansicht = Omit<Listenstand, 'bearbeitetVon'> & {
  readonly bearbeitende: readonly Bearbeitender[];
  readonly zusammengefuehrtIn: { readonly id: string; readonly nummer: string } | null;
  readonly status: Vorgangsstatus;
  readonly notizen: readonly Notizzeile[];
  readonly ereignisse: readonly Ereigniszeile[];
  readonly folgeaktionen: readonly Folgeaktionszeile[];
  /** Je ausgehender Nachricht (Kennung) der Stand im Postausgang. */
  readonly versand: Readonly<Record<string, Versandzeile>>;
  /** SUP-19: ob das Postfach senden kann (aktiv und Zugangsdaten hinterlegt). */
  readonly versandbereit: boolean;
  /** SUP-22, ERR-13: für die Prüfung auf gleichzeitige Antwort. */
  readonly stand: string;
};

/** SUP-20: ein Textbaustein. */
export type Textbaustein = {
  readonly id: string;
  /** Kennung des Postfachs; null: für alle. */
  readonly postfach: string | null;
  readonly kategorie: Kategorie | null;
  readonly titel: string;
  readonly betreff: string | null;
  readonly text: string;
  readonly aktiv: boolean;
  readonly geaendertAm: string;
};

/** SUP-20: ein Textbaustein, für einen Vorgang ausgefüllt. */
export type AusgefuellterBaustein = {
  readonly betreff: string | null;
  readonly text: string;
  /** Platzhalter ohne Wert im Vorgang — sie stehen als [[name]] im Text. */
  readonly offen: readonly string[];
};

/** SUP-19: die Vorbelegung des Antwortentwurfs. */
export type Antwortvorlage = {
  readonly an: readonly string[];
  readonly kopie: readonly string[];
  readonly betreff: string;
  /** Das Verlaufszitat — nur Kundenverkehr, nie Notizen (SUP-21). */
  readonly zitat: string;
  /** Anhänge eingehender Nachrichten, die mitgeschickt werden können. */
  readonly anhaenge: readonly Anhangszeile[];
  readonly absender: string;
};

/** SUP-18: wem ein Vorgang zugewiesen werden kann. */
export type Zuweisbare = {
  readonly benutzer: readonly { readonly id: string; readonly name: string; readonly abwesend: boolean }[];
  readonly rollen: readonly { readonly kennung: string; readonly bezeichnung: string }[];
};

/** Eine hochgeladene Datei für die Antwort, als Base64 (SUP-19). */
export type Antwortdatei = { readonly dateiname: string; readonly inhaltstyp: string; readonly inhaltBase64: string };

export type Antwortentwurf = {
  readonly an: readonly string[];
  readonly kopie: readonly string[];
  readonly betreff: string;
  readonly text: string;
  readonly zitat: boolean;
  /** Kennungen von Anhängen aus dem Vorgang. */
  readonly anhaenge: readonly string[];
  readonly dateien: readonly Antwortdatei[];
  /** Der Stand der Ansicht, auf der der Entwurf entstand (SUP-22). */
  readonly stand: string;
  /** Der Status danach; fehlt er, setzt der Server „wartet auf Kunde". */
  readonly status?: Vorgangsstatus;
  /** SUP-33, SUP-40: der KI-Entwurf, aus dem die Antwort entstand. */
  readonly ki?: { readonly vorschlag: string; readonly geldzusageBestaetigt: boolean };
};

export async function bearbeitung(id: string, hintergrund = false): Promise<Bearbeitungsansicht> {
  // BEN-07: Das Nachladen im Takt haelt die Sitzung nicht wach.
  return rufe(`${vorgangspfad(id)}/bearbeitung`, { hintergrund });
}

/** SUP-18: den Vorgang sich selbst zuweisen. */
export async function vorgangUebernehmen(id: string): Promise<Bearbeitungsansicht> {
  return rufe(`${vorgangspfad(id)}/uebernehmen`, { verfahren: 'POST' });
}

/** SUP-18: zuweisen (Person, Team oder niemand) und Fälligkeit; fehlende Felder bleiben, wie sie sind. */
export async function zuweisen(
  id: string,
  aenderung: { readonly benutzer?: string | null; readonly rolle?: string | null; readonly faelligAm?: string | null },
): Promise<void> {
  await rufe(`${vorgangspfad(id)}/zuweisung`, { verfahren: 'PUT', rumpf: aenderung });
}

/** SUP-14: die Kategorie setzen oder entfernen. */
export async function kategorieSetzen(id: string, kategorie: Kategorie | null): Promise<void> {
  await rufe(`${vorgangspfad(id)}/kategorie`, { verfahren: 'PUT', rumpf: { kategorie } });
}

export async function zuweisbare(): Promise<Zuweisbare> {
  return rufe('/support/zuweisbare');
}

/** SUP-22: „ich habe den Vorgang offen" — jede Minute, solange die Ansicht steht. */
export async function oeffnenMelden(id: string, hintergrund = false): Promise<void> {
  // BEN-07: Die Erneuerung im Takt haelt die Sitzung nicht wach.
  await rufe(`${vorgangspfad(id)}/oeffnen`, { verfahren: 'POST', hintergrund });
}

/** SUP-22: beim Verlassen der Ansicht. */
export async function oeffnenBeenden(id: string): Promise<void> {
  await rufe(`${vorgangspfad(id)}/oeffnen`, { verfahren: 'DELETE' });
}

/** SUP-15: diesen Vorgang in einen anderen desselben Postfachs aufgehen lassen. */
export async function zusammenfuehren(id: string, ziel: string): Promise<{ readonly vorgang: string; readonly nummer: string }> {
  return rufe(`${vorgangspfad(id)}/zusammenfuehren`, { verfahren: 'POST', rumpf: { ziel } });
}

/** SUP-15: eine eingehende Nachricht als eigenen Vorgang abtrennen. */
export async function aufteilen(nachricht: string): Promise<{ readonly vorgang: string; readonly nummer: string }> {
  return rufe(`/support/nachrichten/${encodeURIComponent(nachricht)}/aufteilen`, { verfahren: 'POST' });
}

export async function antwortvorlage(id: string): Promise<Antwortvorlage> {
  return rufe(`${vorgangspfad(id)}/antwortvorlage`);
}

/** SUP-19: die Antwort in den Postausgang; der Versand läuft im Hintergrund. */
export async function antworten(id: string, entwurf: Antwortentwurf): Promise<{ readonly nachricht: string }> {
  return rufe(`${vorgangspfad(id)}/antworten`, { verfahren: 'POST', rumpf: entwurf });
}

/** SUP-21: eine interne Notiz. */
export async function notizAnlegen(id: string, text: string): Promise<Notizzeile> {
  return rufe(`${vorgangspfad(id)}/notizen`, { verfahren: 'POST', rumpf: { text } });
}

/**
 * SUP-20: die aktiven Textbausteine zur Auswahl in der Antwort, für das
 * Postfach (und die Kategorie, wenn gesetzt). Der Server liefert eine Liste
 * oder `{ bausteine }` — beides wird angenommen.
 */
export async function bausteinauswahl(postfach: string, kategorie: Kategorie | null): Promise<readonly Textbaustein[]> {
  const abfrage = new URLSearchParams({ postfach, aktiv: 'ja' });
  if (kategorie !== null) abfrage.set('kategorie', kategorie);
  const antwort = await rufe<readonly Textbaustein[] | { readonly bausteine: readonly Textbaustein[] }>(
    `/support/textbausteine?${abfrage.toString()}`,
  );
  return Array.isArray(antwort) ? antwort : (antwort as { readonly bausteine: readonly Textbaustein[] }).bausteine;
}

/** SUP-20: einen Baustein mit den Werten des Vorgangs ausfüllen. */
export async function bausteinAusfuellen(id: string, baustein: string): Promise<AusgefuellterBaustein> {
  return rufe(`${vorgangspfad(id)}/textbausteine/${encodeURIComponent(baustein)}`);
}

/** SUP-23: eine Folgeaktion anstoßen, bevor die Zielmaske aufgeht. */
export async function folgeaktionAnlegen(id: string, art: Folgeaktionsart): Promise<Folgeaktionszeile> {
  return rufe(`${vorgangspfad(id)}/folgeaktionen`, { verfahren: 'POST', rumpf: { art } });
}

/** SUP-23: was aus der Folgeaktion entstand — Angebot, Buchung, Rechnung. */
export async function folgeaktionErgebnis(
  folgeaktion: string,
  zielArt: Folgeaktionsziel,
  ziel: string,
): Promise<Folgeaktionszeile> {
  return rufe(`/support/folgeaktionen/${encodeURIComponent(folgeaktion)}/ergebnis`, {
    verfahren: 'PUT',
    rumpf: { zielArt, ziel },
  });
}

/** SUP-23, KUN-15: eine Wiedervorlage aus dem Vorgang. */
export async function wiedervorlageAnlegen(
  id: string,
  angaben: { readonly betreff: string; readonly faelligAm: string; readonly beschreibung?: string },
): Promise<Folgeaktionszeile> {
  return rufe(`${vorgangspfad(id)}/wiedervorlage`, { verfahren: 'POST', rumpf: angaben });
}

// ---------------------------------------------------------------------------
// Die KI im Postfach (SUP-14, SUP-29 bis SUP-40, KI-01 bis KI-08; CRM-179)
// ---------------------------------------------------------------------------

/** KI-07: ob die Funktion geht, und wenn nicht, warum. */
export type KiVerfuegbarkeit = { readonly verfuegbar: true } | { readonly verfuegbar: false; readonly grund: string; readonly hinweis: string };

export type KiSchrittart = Folgeaktionsart | 'rueckfrage';

/** SUP-14: ein nächster Schritt — die KI schlägt vor, ausgeführt wird nichts (KI-02). */
export type KiSchritt = {
  readonly aktion: KiSchrittart;
  readonly text: string;
  readonly ziel: { readonly art: 'buchung' | 'rechnung'; readonly nummer: string } | null;
  readonly vorbelegung: { readonly teilnehmer: number | null; readonly termin: string | null };
};

export type Mailauslesung = {
  readonly vorgang: string;
  readonly anliegen: string;
  readonly kategorie: Kategorie;
  readonly sprache: string;
  readonly stimmung: 'positiv' | 'neutral' | 'unzufrieden' | 'veraergert';
  readonly dringlichkeit: 'niedrig' | 'normal' | 'hoch';
  readonly dringlichkeitGrund: string;
  readonly daten: {
    readonly termine: readonly { readonly datum: string; readonly uhrzeit: string | null; readonly bedeutung: string }[];
    readonly fristen: readonly { readonly datum: string; readonly bedeutung: string }[];
    readonly personen: readonly { readonly name: string; readonly rolle: string }[];
    readonly nummern: readonly { readonly art: string; readonly nummer: string }[];
    readonly betraege: readonly { readonly betrag: string; readonly bedeutung: string }[];
  };
  readonly schritte: readonly KiSchritt[];
  /** SUP-39: nur ohne Kunden, und nur aus den Kandidaten. */
  readonly zuordnung: { readonly kunde: { readonly kundennummer: string; readonly name: string } | null; readonly grund: string } | null;
};

export type KiAntwortentwurf = {
  readonly vorgang: string;
  readonly sprache: string;
  readonly text: string;
  /** SUP-33: Sätze mit Geldwirkung — sie brauchen beim Senden die eigene Bestätigung. */
  readonly geldzusagen: readonly string[];
};

export type KiPostfachstand = {
  readonly auslesung: KiVerfuegbarkeit;
  readonly antwort: KiVerfuegbarkeit;
  readonly letzteAuslesung: KiVorschlag<Mailauslesung> | null;
  readonly offenerEntwurf: KiVorschlag<KiAntwortentwurf> | null;
};

export async function kiPostfachstand(id: string): Promise<KiPostfachstand> {
  return rufe(`${vorgangspfad(id)}/ki`);
}

export async function kiAuslesen(id: string): Promise<KiVorschlag<Mailauslesung>> {
  return rufe(`${vorgangspfad(id)}/ki/auslesung`, { verfahren: 'POST', rumpf: {} });
}

export async function kiEntwerfen(id: string): Promise<KiVorschlag<KiAntwortentwurf>> {
  return rufe(`${vorgangspfad(id)}/ki/entwurf`, { verfahren: 'POST', rumpf: {} });
}

/** KI-04: eine Auslesung übernehmen oder verwerfen; ein Entwurf nur verwerfen — übernommen wird er beim Senden. */
export async function kiEntscheiden(vorschlag: string, art: 'uebernehmen' | 'verwerfen'): Promise<void> {
  await rufe(`/support/ki/vorschlaege/${encodeURIComponent(vorschlag)}/entscheidung`, { verfahren: 'PUT', rumpf: { art } });
}

/** KI-06, SUP-37, SUP-38: die Schalter je Postfach. */
export type PostfachKi = {
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly kundenkreis: 'firmenkunden' | 'privatkunden' | null;
  readonly aktiv: boolean;
  readonly kiAktiv: boolean;
  readonly kiAntwortentwurf: boolean;
  readonly kiTonalitaet: string | null;
};

export async function kiPostfaecher(): Promise<readonly PostfachKi[]> {
  return (await rufe<{ readonly postfaecher: readonly PostfachKi[] }>('/support/ki/postfaecher')).postfaecher;
}

export async function kiPostfachSetzen(
  kennung: string,
  e: { readonly kiAktiv: boolean; readonly kiAntwortentwurf: boolean; readonly kiTonalitaet: string | null },
): Promise<PostfachKi> {
  return rufe(`/support/ki/postfaecher/${encodeURIComponent(kennung)}`, { verfahren: 'PUT', rumpf: e });
}
